import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import mermaid from 'mermaid';
import {
  GitBranch,
  Plus,
  Sparkles,
  Download,
  Trash2,
  RefreshCw,
  Loader2,
  Eye,
  Copy,
  Share2,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    primaryColor: '#1e3a5f',
    primaryTextColor: '#fff',
    primaryBorderColor: '#2d4a6f',
    lineColor: '#64748b',
    secondaryColor: '#c9a962',
    tertiaryColor: '#f8fafc'
  },
  flowchart: { htmlLabels: true, curve: 'basis' }
});

export default function CaseWorkflowTab({ caseId, caseName }) {
  const { token } = useAuth();
  const mermaidRef = useRef(null);
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', template_id: '' });

  useEffect(() => {
    fetchWorkflows();
  }, [token]);

  useEffect(() => {
    if (selectedWorkflow?.mermaid_syntax) {
      renderMermaid(selectedWorkflow.mermaid_syntax);
    }
  }, [selectedWorkflow]);

  const fetchWorkflows = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [wfRes, tplRes] = await Promise.all([
        fetch(`${API_URL}/api/workflow/`, { headers }),
        fetch(`${API_URL}/api/workflow/templates`, { headers })
      ]);
      if (wfRes.ok) setWorkflows(await wfRes.json());
      if (tplRes.ok) setTemplates(await tplRes.json());
    } catch (error) {
      console.error('Error fetching workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderMermaid = async (syntax) => {
    if (mermaidRef.current) {
      try {
        mermaidRef.current.innerHTML = '';
        const id = `mermaid-${Date.now()}`;
        const { svg } = await mermaid.render(id, syntax);
        mermaidRef.current.innerHTML = svg;
      } catch (err) {
        mermaidRef.current.innerHTML = `<pre class="text-xs text-muted-foreground p-4">${syntax}</pre>`;
      }
    }
  };

  const handleCreateWorkflow = async () => {
    if (!createForm.name.trim()) {
      toast.error('Please enter a workflow name');
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/workflow/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: createForm.name,
          description: createForm.description || `Workflow for case: ${caseName}`,
          template_id: createForm.template_id || undefined
        })
      });
      if (res.ok) {
        const result = await res.json();
        toast.success('Workflow created');
        setShowCreateDialog(false);
        setCreateForm({ name: '', description: '', template_id: '' });
        fetchWorkflows();
        setSelectedWorkflow(result);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create workflow');
      }
    } catch {
      toast.error('Failed to create workflow');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!selectedWorkflow) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/workflow/${selectedWorkflow.id}/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setSelectedWorkflow(result);
        setWorkflows(workflows.map(w => w.id === result.id ? result : w));
        toast.success('AI diagram generated');
      }
    } catch {
      toast.error('AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    try {
      const res = await fetch(`${API_URL}/api/workflow/workflows/${workflowId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setWorkflows(workflows.filter(w => w.id !== workflowId));
        if (selectedWorkflow?.id === workflowId) setSelectedWorkflow(null);
        toast.success('Workflow deleted');
      }
    } catch {
      toast.error('Failed to delete workflow');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="case-workflow-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-heading font-semibold">Workflow Diagrams</h3>
          <p className="text-sm text-muted-foreground">AI-generated visual workflow charts</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-workflow-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Workflow
        </Button>
      </div>

      {workflows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <GitBranch className="w-10 h-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No workflow diagrams yet</p>
            <Button className="mt-4" variant="outline" onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Workflow
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Workflow List */}
          <div className="lg:col-span-1 space-y-2">
            {workflows.map(wf => (
              <Card
                key={wf.id}
                className={`cursor-pointer transition-colors hover:border-navy/30 ${selectedWorkflow?.id === wf.id ? 'border-navy bg-navy/5' : ''}`}
                onClick={() => setSelectedWorkflow(wf)}
                data-testid={`workflow-${wf.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{wf.name}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{wf.description}</p>
                    </div>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                      onClick={(e) => { e.stopPropagation(); handleDeleteWorkflow(wf.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Workflow Detail */}
          <div className="lg:col-span-2">
            {selectedWorkflow ? (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{selectedWorkflow.name}</CardTitle>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleGenerateAI} disabled={generating}>
                        {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                        Regenerate
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div
                    ref={mermaidRef}
                    className="bg-muted/30 rounded-lg p-4 min-h-[300px] overflow-auto"
                    data-testid="mermaid-diagram"
                  />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-20">
                  <p className="text-sm text-muted-foreground">Select a workflow to view its diagram</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Workflow</DialogTitle>
            <DialogDescription>Create a new workflow diagram for this case</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Workflow Name</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="e.g., Discovery Process, Trial Preparation"
                data-testid="workflow-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="Describe the workflow process..."
                rows={3}
              />
            </div>
            {templates.length > 0 && (
              <div>
                <Label>Start from Template (optional)</Label>
                <Select value={createForm.template_id} onValueChange={(v) => setCreateForm({ ...createForm, template_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Choose a template..." /></SelectTrigger>
                  <SelectContent>
                    {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateWorkflow} disabled={generating || !createForm.name.trim()} data-testid="submit-workflow-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
